import { createMuiTheme, createStyles } from '@material-ui/core/styles';
import './theme.css';

import {
	BASE_BG_HOVER_COLOR,
	BASE_BG_COLOR,
	PRIMARY_BG_COLOR,
	SUCCESS_BG_COLOR,
	PRIMARY_BG_HOVER_COLOR,
	DANGER_BG_COLOR,
	SUCCESS_BG_HOVER_COLOR,
	DANGER_BG_HOVER_COLOR,
	BASE_LINE_HEIGHT,
	FONT_WEIGHT_BOLD,
	H1_SIZE,
	H1_WEIGHT,
	H2_WEIGHT,
	H2_SIZE,
	HEADING_LINE_HEIGHT,
	H3_SIZE,
	H4_SIZE,
	H5_SIZE,
	H6_SIZE,
	FONT_FAMILY,
	FONT_WEIGHT_REGULAR,
	TEXT_SIZE_SM,
	TEXT_SIZE_LG,
	TEXT_SIZE_MD,
	INPUT_BTN_HEIGHT_MD,
	INPUT_BTN_PADDING_X_MD,
	PARAGRAPH_MARGIN_BOTTOM,
	HEADING_MARGIN_BOTTOM,
	BTN_BASE_BORDER_COLOR,
	COLOR_GRAY_10,
	BTN_BASE_BG_COLOR,
	INPUT_BTN_HEIGHT_LG,
	INPUT_BTN_HEIGHT_SM,
	INPUT_BTN_PADDING_X_LG,
	INPUT_BTN_PADDING_X_SM,
	TABS_BASE_BORDER_COLOR,
	TABS_BASE_BORDER_WIDTH,
	TAB_BASE_BORDER_ACTIVE_COLOR,
	TAB_BASE_BORDER_ACTIVE_WIDTH,
	TAB_BASE_BG_HOVER_COLOR,
	BASE_BORDER_WIDTH,
	COLOR_RED_30,
	COLOR_RED_05,
	COLOR_GREEN_30,
	COLOR_GREEN_05,
	DANGER_TEXT_COLOR,
	DANGER_BG_LIGHT_COLOR,
	DANGER_BORDER_COLOR,
	SUCCESS_TEXT_COLOR,
	BASE_TEXT_SIZE,
	BTN_LINK_TEXT_COLOR,
} from './tokens';

const theme = createMuiTheme({
	palette: {
		primary: {
			main: PRIMARY_BG_COLOR,
			dark: PRIMARY_BG_HOVER_COLOR,
		},
		secondary: {
			main: BASE_BG_COLOR,
			dark: BASE_BG_HOVER_COLOR,
		},
		error: {
			main: DANGER_BG_COLOR,
			dark: DANGER_BG_HOVER_COLOR,
		},
		success: {
			main: SUCCESS_BG_COLOR,
			dark: SUCCESS_BG_HOVER_COLOR,
		},
		link: BTN_LINK_TEXT_COLOR,
	},
	typography: {
		fontFamily: FONT_FAMILY,
		fontWeightRegular: FONT_WEIGHT_REGULAR,
		useNextVariants: true,
		htmlFontSize: 16,
		lineHeight: BASE_LINE_HEIGHT,
		h1: {
			fontWeight: H1_WEIGHT,
			fontSize: H1_SIZE,
			lineHeight: HEADING_LINE_HEIGHT,
		},
		h2: {
			fontWeight: H2_WEIGHT,
			fontSize: H2_SIZE,
			lineHeight: HEADING_LINE_HEIGHT,
		},
		h3: {
			fontWeight: FONT_WEIGHT_BOLD,
			fontSize: H3_SIZE,
			lineHeight: HEADING_LINE_HEIGHT,
		},
		h4: {
			fontWeight: FONT_WEIGHT_BOLD,
			fontSize: H4_SIZE,
			lineHeight: HEADING_LINE_HEIGHT,
		},
		h5: {
			fontWeight: FONT_WEIGHT_BOLD,
			fontSize: H5_SIZE,
			lineHeight: HEADING_LINE_HEIGHT,
		},
		h6: {
			fontWeight: FONT_WEIGHT_BOLD,
			fontSize: H6_SIZE,
			lineHeight: HEADING_LINE_HEIGHT,
		},
		subtitle1: {
			fontWeight: FONT_WEIGHT_BOLD,
			fontSize: TEXT_SIZE_LG,
		},
		subtitle2: {
			fontWeight: FONT_WEIGHT_BOLD,
		},
		body1: {
			fontWeight: FONT_WEIGHT_REGULAR,
			fontSize: TEXT_SIZE_MD,
		},
		body2: {
			fontWeight: FONT_WEIGHT_REGULAR,
			fontSize: TEXT_SIZE_LG,
		},
		caption: {
			fontWeight: FONT_WEIGHT_REGULAR,
			fontSize: TEXT_SIZE_SM,
		},
		overline: {
			fontWeight: FONT_WEIGHT_BOLD,
			fontSize: TEXT_SIZE_SM,
		},
		button: {
			textTransform: 'none',
			fontSize: TEXT_SIZE_MD,
		},
	},
	props: {
		MuiButtonBase: {
			disableRipple: true,
			disableTouchRipple: true,
			focusRipple: false,
		},
		MuiButton: {
			disableElevation: true,
		},
	},
	overrides: {
		MuiLink: {
			root: {
				color: BTN_LINK_TEXT_COLOR,
			}
		},
		MuiTypography: {
			h1: {
				'&.MuiTypography-gutterBottom': {
					marginBottom: HEADING_MARGIN_BOTTOM,
				},
			},
			h2: {
				'&.MuiTypography-gutterBottom': {
					marginBottom: HEADING_MARGIN_BOTTOM,
				},
			},
			h3: {
				'&.MuiTypography-gutterBottom': {
					marginBottom: HEADING_MARGIN_BOTTOM,
				},
			},
			h4: {
				'&.MuiTypography-gutterBottom': {
					marginBottom: HEADING_MARGIN_BOTTOM,
				},
			},
			h5: {
				'&.MuiTypography-gutterBottom': {
					marginBottom: HEADING_MARGIN_BOTTOM,
				},
			},
			h6: {
				'&.MuiTypography-gutterBottom': {
					marginBottom: HEADING_MARGIN_BOTTOM,
				},
			},
			body1: {
				'&.MuiTypography-gutterBottom': {
					marginBottom: PARAGRAPH_MARGIN_BOTTOM,
				},
			},
			colorError: {
				color: DANGER_TEXT_COLOR,
			}
		},
		// This is required for input fields in IE
		MuiTextField: {
			root: {
				'& input[type=text]::-ms-clear': {
					display: 'none',
				},
				'& input[type=password]::-ms-reveal': {
					display: 'none',
				},
				height: INPUT_BTN_HEIGHT_MD,
			},
		},
		MuiCssBaseline: {
			'@global': {
				body: {
					backgroundColor: '#fff',
				},
			},
		},
		MuiAlert: {
			root: {
				fontSize: BASE_TEXT_SIZE,
			},
			standardError: {
				color: DANGER_TEXT_COLOR,
				backgroundColor: COLOR_RED_05,
				borderColor: COLOR_RED_30,
				borderWidth: BASE_BORDER_WIDTH,
				borderStyle: 'solid',
			},
			standardSuccess: {
				color: SUCCESS_TEXT_COLOR,
				backgroundColor: COLOR_GREEN_05,
				borderColor: COLOR_GREEN_30,
				borderWidth: BASE_BORDER_WIDTH,
				borderStyle: 'solid',
			},
			outlinedError: {
				backgroundColor: DANGER_BG_LIGHT_COLOR,
				borderColor: COLOR_RED_30,
				'& .MuiAlert-icon': {
					color: DANGER_BORDER_COLOR,
				},
			},
			outlinedWarning: {
				background: 'white',
			},
		},
		MuiAutocomplete: createStyles({
			inputRoot: {
				'&[class*="MuiOutlinedInput-root"]': {
					'& .MuiAutocomplete-endAdornment': {
						right: '7px ',
					},
				},
			},
		}),
		MuiAppBar: {
			colorDefault: {
				backgroundColor: COLOR_GRAY_10,
			},
		},
		MuiStepper: {
			root: {
				'&.MuiPaper-root': {
					backgroundColor: 'transparent',
				},
			},
		},
		MuiButton: {
			containedSizeLarge: {
				height: INPUT_BTN_HEIGHT_LG,
				padding: INPUT_BTN_PADDING_X_LG,
				fontSize: TEXT_SIZE_LG,
			},
			containedSizeSmall: {
				height: INPUT_BTN_HEIGHT_SM,
				padding: INPUT_BTN_PADDING_X_SM,
				fontSize: TEXT_SIZE_SM,
			},
			outlinedSizeLarge: {
				height: INPUT_BTN_HEIGHT_LG,
				padding: INPUT_BTN_PADDING_X_LG,
				fontSize: TEXT_SIZE_LG,
			},
			outlinedSizeSmall: {
				height: INPUT_BTN_HEIGHT_SM,
				padding: INPUT_BTN_PADDING_X_SM,
				fontSize: TEXT_SIZE_SM,
			},
			contained: {
				height: INPUT_BTN_HEIGHT_MD,
				padding: INPUT_BTN_PADDING_X_MD,
			},
			outlined: {
				height: INPUT_BTN_HEIGHT_MD,
				padding: INPUT_BTN_PADDING_X_MD,
				backgroundColor: BTN_BASE_BG_COLOR,
				borderColor: BTN_BASE_BORDER_COLOR,
			},
		},
	},
	MuiTabs: {
		fixed: {
			borderBottomStyle: 'solid',
			borderBottomColor: TABS_BASE_BORDER_COLOR,
			borderBottomWidth: TABS_BASE_BORDER_WIDTH,
		},
		indicator: {
			backgroundColor: TAB_BASE_BORDER_ACTIVE_COLOR,
			height: TAB_BASE_BORDER_ACTIVE_WIDTH,
		},
	},
	MuiTab: {
		root: {
			height: INPUT_BTN_HEIGHT_MD,
			padding: INPUT_BTN_PADDING_X_MD,
			'&:hover': {
				backgroundColor: TAB_BASE_BG_HOVER_COLOR,
			},
		},
		textColorInherit: {
			opacity: '1',
		},
	},
});

export default theme;
